import type { LucideIcon } from "lucide-react";
import {
  CheckCheck,
  CircleX,
  Clock,
  Eye,
  Send,
  UserMinus,
  Users,
} from "lucide-react";
import type {
  CampaignResultRecipientRow,
  CampaignResultStatus,
  CampaignResultsSummary,
} from "@multi-chat/shared";
import { resolveWhatsAppDeliveryFailureCopy } from "../whatsapp/whatsapp-delivery-failure-presentation.js";

export const CAMPAIGN_RESULT_FILTER_OPTIONS: Array<CampaignResultStatus | "ALL"> = [
  "ALL",
  "PROCESSING",
  "SENT",
  "DELIVERED",
  "READ",
  "FAILED",
  "EXCLUDED",
];

export type CampaignResultMetricKey = keyof Pick<
  CampaignResultsSummary,
  "total" | "processing" | "sent" | "delivered" | "read" | "failed" | "excluded"
>;

export type CampaignResultMetricTone = "neutral" | "accent" | "success" | "read" | "error" | "warning";

export type CampaignResultMetricCard = {
  key: CampaignResultMetricKey;
  filter: CampaignResultStatus | "ALL";
  labelKey: string;
  icon: LucideIcon;
  tone: CampaignResultMetricTone;
};

export const CAMPAIGN_RESULT_METRIC_CARDS: readonly CampaignResultMetricCard[] = [
  {
    key: "total",
    filter: "ALL",
    labelKey: "campaigns.results.metrics.total",
    icon: Users,
    tone: "neutral",
  },
  {
    key: "processing",
    filter: "PROCESSING",
    labelKey: "campaigns.results.metrics.processing",
    icon: Clock,
    tone: "accent",
  },
  {
    key: "sent",
    filter: "SENT",
    labelKey: "campaigns.results.metrics.sent",
    icon: Send,
    tone: "accent",
  },
  {
    key: "delivered",
    filter: "DELIVERED",
    labelKey: "campaigns.results.metrics.delivered",
    icon: CheckCheck,
    tone: "success",
  },
  {
    key: "read",
    filter: "READ",
    labelKey: "campaigns.results.metrics.read",
    icon: Eye,
    tone: "read",
  },
  {
    key: "failed",
    filter: "FAILED",
    labelKey: "campaigns.results.metrics.failed",
    icon: CircleX,
    tone: "error",
  },
  {
    key: "excluded",
    filter: "EXCLUDED",
    labelKey: "campaigns.results.metrics.excluded",
    icon: UserMinus,
    tone: "warning",
  },
];

export function campaignResultMetricFilter(
  key: CampaignResultMetricKey,
): CampaignResultStatus | "ALL" {
  const card = CAMPAIGN_RESULT_METRIC_CARDS.find((item) => item.key === key);
  return card?.filter ?? "ALL";
}

export function campaignResultMetricToneClass(tone: CampaignResultMetricTone): string {
  switch (tone) {
    case "neutral":
      return "border-line/70 bg-surface-muted/70 text-muted";
    case "accent":
      return "border-accent/20 bg-accent/10 text-accent";
    case "success":
      return "border-success/25 bg-success-muted text-success";
    case "read":
      return "border-success/35 bg-success-muted text-success";
    case "error":
      return "border-error/25 bg-error-muted text-error";
    case "warning":
      return "border-warning/30 bg-warning-muted text-warning";
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

export function campaignResultStatusChipClass(status: CampaignResultStatus): string {
  switch (status) {
    case "READ":
      return "border-success/35 bg-success-muted text-success";
    case "DELIVERED":
      return "border-success/25 bg-success-muted text-success";
    case "SENT":
      return "border-accent/30 bg-accent/10 text-accent";
    case "FAILED":
      return "border-error/30 bg-error-muted text-error";
    case "EXCLUDED":
      return "border-warning/30 bg-warning-muted text-warning";
    case "PROCESSING":
      return "border-accent/20 bg-accent/8 text-accent";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignResultDisplayName(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : "—";
}

export type CampaignResultRowReason = {
  primary: string;
  secondary: string | null;
  technicalCode: string | null;
};

export function campaignResultRowReason(
  t: (key: string, options?: Record<string, unknown>) => string,
  row: CampaignResultRecipientRow,
): CampaignResultRowReason | null {
  if (row.resultStatus === "EXCLUDED" && row.exclusionReasonCode) {
    const key = `campaigns.results.exclusionReasons.${row.exclusionReasonCode}`;
    const translated = t(key);
    return translated === key
      ? null
      : { primary: translated, secondary: null, technicalCode: null };
  }
  if (row.resultStatus === "FAILED" && row.failureCode) {
    const copy = resolveWhatsAppDeliveryFailureCopy(t, row.failureCode);
    if (!copy) return null;
    return {
      primary: copy.primary,
      secondary: copy.secondary,
      technicalCode: copy.technicalCode,
    };
  }
  return null;
}

export function campaignResultsHaveActiveFilters(input: {
  search: string;
  resultStatus: CampaignResultStatus | "ALL";
}): boolean {
  return input.search.trim().length > 0 || input.resultStatus !== "ALL";
}
